import { ResolveFn } from '@angular/router';
import { inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Bundle, Patient } from 'fhir/r4';
import { ErrorService } from '../shared/error.service';
import { authBackendEndPointToken } from '../app.config';
import {AuthService} from '../shared/auth/auth.service';
export interface UserApproval {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phoneNumber?: string;
  accountType: string; // "Patient" or "Practitioner"
  requestedRole: string;
  organizationId?: string;
  fhirResourceId?: string;
  status: string;
  submittedAt: string;
}

export interface PatientRegistrationData {
  pending: UserApproval[];
  approved: Patient[];
  deceased: Patient[];
}

/**
 * Resolver: Fetch ALL patient registration data from appropriate sources
 * - Pending: Auth backend database (UserApprovals table)
 * - Approved: FHIR server (active patients)
 * - Deceased: FHIR server (deceased patients)
 */
export const patientRegistrationCenterResolver: ResolveFn<PatientRegistrationData> = () => {
    const http = inject(HttpClient);
    const errorService = inject(ErrorService);
    const backendApi = inject(authBackendEndPointToken);
    const authService = inject(AuthService);

    const currentUser = authService.getCurrentUser();
    const organizationId = currentUser?.organizationId;

    if (!organizationId) {
        errorService.openandCloseError('No organization assigned to user');
        return of({
            pending: [],
            approved: [],
            deceased: []
        });
    }
    // 1. Fetch pending approvals from auth backend
    const pending$ = http.get<UserApproval[]>(
        `${backendApi}/api/admin/approvals/pending?accountType=Patient`
    ).pipe(
        catchError(err => {
            console.error('Failed to load pending approvals:', err);
            errorService.openandCloseError('Failed to load pending approvals');
            return of([]);
        })
    );

    // 2. Fetch approved patients from FHIR server
    const approved$ = http.get<Bundle>(
        `${backendApi}/fhir/Patient?active=true&_count=1000&_format=json`
    ).pipe(
        map(bundle => {
            const entries = bundle?.entry || [];
            return entries
                .map(e => e.resource as Patient)
                .filter(p =>
                    p.active === true &&
                    !(p as any).deceasedBoolean &&
                    !(p as any).deceasedDateTime
                );
        }),
        catchError(err => {
            console.error('Failed to load approved patients:', err);
            errorService.openandCloseError('Failed to load approved patients');
            return of([]);
        })
    );

    // 3. Fetch deceased patients from FHIR server
    const deceased$ = http.get<Bundle>(
        `${backendApi}/fhir/Patient?_sort=-_lastUpdated&_count=1000&_format=json`
    ).pipe(
        map(bundle => {
            const entries = bundle?.entry || [];
            return entries
                .map(e => e.resource as Patient)
                .filter(p =>
                    (p as any).deceasedBoolean === true ||
                    !!(p as any).deceasedDateTime
                );
        }),
        catchError(err => {
            console.error('Failed to load deceased patients:', err);
            return of([]);
        })
    );

    // Combine all three sources
    return forkJoin({
        pending: pending$,
        approved: approved$,
        deceased: deceased$
    });
};
